import { create } from 'zustand';
import { DeliveryModeV2, ProgramTypeDataV2, createDefaultProgramTypeData } from '@/types/program-v2';
import { draftsV2Api } from '@/lib/api/v2/drafts';
import { transformToAPIFormat, transformFromAPIFormat } from '@/utils/programTransform';
import { toast } from 'sonner';

function isPlainObject(value: unknown): value is Record<string, any> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Deep-merge plain objects. Arrays are replaced (not concatenated).
 * `undefined` means "no change". `null` means "set null" (explicit clear).
 */
function deepMerge<T extends Record<string, any>>(base: T, patch: Partial<T>): T {
  const out: any = { ...base };

  (Object.keys(patch) as Array<keyof T>).forEach((key) => {
    const nextVal = patch[key];

    // undefined => leave existing
    if (nextVal === undefined) return;

    const prevVal = (base as any)[key];

    // null => explicit clear
    if (nextVal === null) {
      out[key as string] = null;
      return;
    }

    // objects => recurse
    if (isPlainObject(prevVal) && isPlainObject(nextVal)) {
      out[key as string] = deepMerge(prevVal, nextVal as any);
      return;
    }

    // primitives / arrays / dates / etc.
    out[key as string] = nextVal as any;
  });

  return out as T;
}

// In-memory store only - no localStorage persistence
// Data persists while navigating between Step 1 and Step 2
// Clears when navigating away or after successful creation

interface ProgramData {
  // Step 1 - Common fields (fields moved to ProgramTypeDataV2: subtitle, about, videoUrl, nationalCertifications, podcastData, thisCourseIncludesData, programComparison)
  title: string;
  /** Optional: student-facing URL identifier. Backend normalizes + enforces uniqueness. */
  slug?: string;
  /** Mega-menu program tile (1:1); program root override. */
  navThumbnailUrl?: string | null;
  categoryId: string;
  duration?: { value?: number; isInWeeks?: boolean };
  theoryDuration?: { value?: number; isInWeeks?: boolean };
  language: "english" | "spanish";
  status: "active" | "inactive";
  isPublished: boolean;
  platform?: { isIspring?: boolean; contentTitle?: string; contentItemId?: string };
  socialMedia: { 
    facebook?: string; 
    twitter?: string; 
    instagram?: string; 
    linkedin?: string; 
    youtube?: string; 
    website?: string; 
  };
  whoisthisFor: Array<{ id: number; category: string; title: string; description: string }>;
  wherecaniWork: Array<{ id: number; title: string; description: string }>;
  clinicalProceduresGrouped: Array<{ 
    label: string; 
    subHeading?: string; 
    items: Array<{ id: string; title: string }> 
  }>;
  hasclinical: boolean;  // Flag to indicate if program has clinical components
  clinicalsScheduling: {
    schedulingWindowDays: number;
    reactivationFeeCents: number;
    rescheduleFeeFirstMissCents: number;
    rescheduleFeeSecondMissCents: number;
    rescheduleFeeThirdMissCents: number;
    timezone: string;
    formVariant?: "standard_v1";
  };
  certificationExamScheduling: {
    onCampusUrl: string;
    onlineUrl: string;
    schedulingWindowDays?: number;
  };
  modules: Array<{ 
    label: string; 
    order: number; 
    isActive: boolean; 
    items: Array<{ id: string; title: string; subtitle: string; isExam: boolean }> 
  }>;
  premiumShareCount: number;
  flyerTheme: 'black' | 'red' | 'blue' | 'green' | 'purple' | 'rose';
  marketingTags: string[];
  premiumDescription?: string;
  premiumTagline?: string;
  premiumRating?: number;
  premiumReviewCount?: number;
  premiumStudentCount: number;
  premiumViewCount: number;
  premiumLikeCount: number;
  
  // Step 2 - Program type specific
  activeProgramType: DeliveryModeV2;
  programTypes: {
    hybrid?: ProgramTypeDataV2;
    'via-zoom'?: ProgramTypeDataV2;
    'on-campus'?: ProgramTypeDataV2;
    online?: ProgramTypeDataV2;
  };
  
  // Edit mode tracking
  loadedProgramId: string | null;

  // Draft fields
  draftId: string | null;
  isDraftMode: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
  isSaving: boolean;

  // Actions
  updateField: (field: string, value: any) => void;
  updateNestedField: (path: string, value: any) => void;
  setActiveProgramType: (type: DeliveryModeV2) => void;
  updateProgramType: (type: DeliveryModeV2, data: Partial<ProgramTypeDataV2>) => void;
  removeProgramType: (type: DeliveryModeV2) => void;
  reset: () => void;
  
  // Draft actions
  saveDraft: () => Promise<void>;
  loadDraft: (draftId: string) => Promise<void>;
  publishDraft: () => Promise<{ success: boolean; programId?: string; error?: string }>;
  setHasUnsavedChanges: (value: boolean) => void;
}

export const useProgramStore = create<ProgramData>((set, get) => ({
  // Initial state (combined Step 1 and Step 2 defaults)
  title: '',
  slug: '',
  navThumbnailUrl: null,
  categoryId: '',
  duration: undefined,
  theoryDuration: undefined,
  language: 'english',
  status: 'active',
  isPublished: false,
  platform: { isIspring: false },
  socialMedia: { 
    facebook: '', 
    twitter: '', 
    instagram: '', 
    linkedin: '', 
    youtube: '', 
    website: '' 
  },
  whoisthisFor: [],
  wherecaniWork: [],
  clinicalProceduresGrouped: [],
  hasclinical: false,
  clinicalsScheduling: {
    schedulingWindowDays: 30,
    reactivationFeeCents: 5000,
    rescheduleFeeFirstMissCents: 2500,
    rescheduleFeeSecondMissCents: 4500,
    rescheduleFeeThirdMissCents: 7500,
    timezone: "America/New_York",
    formVariant: "standard_v1",
  },
  certificationExamScheduling: {
    onCampusUrl: "https://portal.aeca.com/aeca/1200/register-user/6862971238929789c5eaf968",
    onlineUrl: "https://portal.aeca.com/aeca/1201/register-user/6862971238929789c5eaf968",
    schedulingWindowDays: 60,
  },
  modules: [],
  premiumShareCount: 0,
  flyerTheme: 'black',
  marketingTags: [],
  premiumDescription: '',
  premiumTagline: '',
  premiumRating: 0,
  premiumReviewCount: 0,
  premiumStudentCount: 0,
  premiumViewCount: 0,
  premiumLikeCount: 0,
  
  // Program type management
  activeProgramType: 'hybrid',
  programTypes: {
    hybrid: createDefaultProgramTypeData()
  },
  
  // Edit mode tracking
  loadedProgramId: null,
  
  // Draft fields
  draftId: null,
  isDraftMode: false,
  lastSaved: null,
  hasUnsavedChanges: false,
  isSaving: false,
  
  // Actions
  updateField: (field: string, value: any) => set(state => ({
    ...state,
    [field]: value
  })),
  
  updateNestedField: (path: string, value: any) => set(state => {
    const newState = { ...state };
    setNestedValue(newState, path, value);
    return newState;
  }),
  
  setActiveProgramType: (type: DeliveryModeV2) => set(state => {
    console.log('🔍 DEBUG - Setting active program type to:', type);
    console.log('🔍 DEBUG - Current program types:', state.programTypes);
    
    const newProgramTypes = { ...state.programTypes };
    if (!newProgramTypes[type]) {
      console.log('🔍 DEBUG - Initializing new program type:', type);
      newProgramTypes[type] = createDefaultProgramTypeData();
    }
    
    console.log('🔍 DEBUG - Updated program types:', newProgramTypes);
    
    return {
      activeProgramType: type,
      programTypes: newProgramTypes
    };
  }),
  
  updateProgramType: (type: DeliveryModeV2, data: Partial<ProgramTypeDataV2>) => set(state => {
    console.log('🔍 DEBUG - Updating program type:', type, 'with data:', data);
    console.log('🔍 DEBUG - Current program types:', state.programTypes);
    
    const newProgramTypes = { ...state.programTypes };
    if (!newProgramTypes[type]) {
      console.log('🔍 DEBUG - Initializing new program type:', type);
      newProgramTypes[type] = createDefaultProgramTypeData();
    }

    const prev = newProgramTypes[type]!;
    // Important: shallow merge breaks nested schedule updates (missing keys won't clear nested fields).
    // Deep-merge `schedule` when partial schedule updates are sent.
    const nextSchedule =
      data.schedule !== undefined
        ? deepMerge((prev.schedule as any) || {}, data.schedule as any)
        : prev.schedule;

    const { schedule, ...rest } = data as any;
    newProgramTypes[type] = {
      ...deepMerge(prev as any, rest),
      ...(data.schedule !== undefined ? { schedule: nextSchedule } : {}),
    } as ProgramTypeDataV2;
    
    console.log('🔍 DEBUG - Updated program types:', newProgramTypes);
    
    return {
      programTypes: newProgramTypes
    };
  }),

  removeProgramType: (type: DeliveryModeV2) => set(state => {
    console.log('🔍 DEBUG - Removing program type:', type);
    
    const newProgramTypes = { ...state.programTypes };
    delete newProgramTypes[type];
    
    let newActiveType = state.activeProgramType;
    if (state.activeProgramType === type) {
      const remainingTypes = Object.keys(newProgramTypes) as DeliveryModeV2[];
      newActiveType = remainingTypes[0] || 'hybrid';
    }
    
    console.log('🔍 DEBUG - New active type:', newActiveType);
    console.log('🔍 DEBUG - Remaining program types:', newProgramTypes);
    
    return {
      programTypes: newProgramTypes,
      activeProgramType: newActiveType
    };
  }),
  
  reset: () => set({
    title: '',
    slug: '',
    navThumbnailUrl: null,
    categoryId: '',
    duration: undefined,
    theoryDuration: undefined,
    language: 'english',
    status: 'active',
    isPublished: false,
    platform: { isIspring: false },
    socialMedia: { 
      facebook: '', 
      twitter: '', 
      instagram: '', 
      linkedin: '', 
      youtube: '', 
      website: '' 
    },
    whoisthisFor: [],
    wherecaniWork: [],
    clinicalProceduresGrouped: [],
  hasclinical: false,
  clinicalsScheduling: {
    schedulingWindowDays: 30,
    reactivationFeeCents: 5000,
    rescheduleFeeFirstMissCents: 2500,
    rescheduleFeeSecondMissCents: 4500,
    rescheduleFeeThirdMissCents: 7500,
    timezone: "America/New_York",
    formVariant: "standard_v1" as const,
  },
  certificationExamScheduling: {
    onCampusUrl: "https://portal.aeca.com/aeca/1200/register-user/6862971238929789c5eaf968",
    onlineUrl: "https://portal.aeca.com/aeca/1201/register-user/6862971238929789c5eaf968",
    schedulingWindowDays: 60,
  },
  modules: [],
  premiumShareCount: 0,
    flyerTheme: 'black',
    marketingTags: [],
    premiumDescription: '',
    premiumTagline: '',
    premiumRating: 0,
    premiumReviewCount: 0,
    premiumStudentCount: 0,
    premiumViewCount: 0,
    premiumLikeCount: 0,
    activeProgramType: 'hybrid',
    programTypes: {
      hybrid: createDefaultProgramTypeData()
    },
    loadedProgramId: null,
    draftId: null,
    isDraftMode: false,
    lastSaved: null,
    hasUnsavedChanges: false,
    isSaving: false
  }),
 
  saveDraft: async () => {
    const state = get();
    set({ isSaving: true });
    
    try {
      const payload = transformToAPIFormat(state);
      
      let response;
      if (state.draftId) {
        response = await draftsV2Api.update(state.draftId, payload);
        toast.success('Draft updated successfully');
      } else {
        response = await draftsV2Api.create(payload);
        toast.success(`Draft created! ID: ${response.draftId}`);
      }
      
      set({
        draftId: response.draftId || state.draftId,
        isDraftMode: true,
        lastSaved: new Date(),
        hasUnsavedChanges: false,
        isSaving: false,
      });
      
    } catch (error: any) {
      console.error('Error saving draft:', error);
      toast.error(error.message || 'Failed to save draft');
      set({ isSaving: false });
      throw error;
    }
  },
  
  loadDraft: async (draftId: string) => {
    try {
      const response = await draftsV2Api.get(draftId);
      
      if (response.success && response.data) {
        const storeData = transformFromAPIFormat(response.data);
        
        set({
          ...storeData,
          isDraftMode: true,
          hasUnsavedChanges: false,
        });
        
        toast.success('Draft loaded successfully');
      }
    } catch (error: any) {
      console.error('Error loading draft:', error);
      toast.error(error.message || 'Failed to load draft');
      throw error;
    }
  },
  
  publishDraft: async () => {
    const state = get();
    
    try {
      await get().saveDraft();
      
      const response = await draftsV2Api.publish(state.draftId!);
      
      if (response.success) {
        toast.success(`Program published! ID: ${response.data?.customProgramId}`);
        
        get().reset();
        
        return {
          success: true,
          programId: response.data?.customProgramId,
        };
      } else {
        return {
          success: false,
          error: response.message,
        };
      }
    } catch (error: any) {
      console.error('Error publishing draft:', error);
      const code =
        error?.responseBody?.code ||
        error?.responseBody?.errorCode ||
        error?.responseBody?.error;
      if (code === 'SLUG_TAKEN') {
        return {
          success: false,
          error: 'Slug already used. Please choose a different slug.',
        };
      }
      return {
        success: false,
        error: error.message || 'Failed to publish program',
      };
    }
  },
  
  setHasUnsavedChanges: (value: boolean) => set({ hasUnsavedChanges: value }),
}));

// Helper function for nested updates
function setNestedValue(obj: any, path: string, value: any) {
  const keys = path.split('.');
  let current = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    if (!current[keys[i]]) current[keys[i]] = {};
    current = current[keys[i]];
  }
  current[keys[keys.length - 1]] = value;
}